import { useTranslations } from 'next-intl';

import { MovieDto as MovieV1Dto } from '@cineamo/api/models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { ShowingDto, ShowingGroupedByDateTimeDto } from '@cineamo/api/models/showing/ShowingDto.types';

type MovieDto = MovieV1Dto | MovieV2Dto;

import { Client } from '@cineamo/api/constants/Client.enum';

import { getLocalizedContentDescription } from '@cineamo/api/helpers/content-helper';
import {
    getMovieBackdropBlurDataImage,
    getMovieBackdropImage,
    getMovieOverview,
    getMoviePosterBlurDataImage,
    getMoviePosterImage,
    getMovieTitle,
    MovieImageOptions
} from '@cineamo/api/helpers/movie-helper';

import { isDatetimePast } from '@/helpers/time-helper';
import { ISO_3166_1_LOCALE, ISO_639_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';
import { formatDuration, getOnlineTicketUrl, isShowingSalePending } from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';

import { formatDate_yyyyMMdd } from '@/utils/date-helper';

import { CountryCode } from './locale-helper';

export type ShowingImageFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    useBackdropAsFallback?: boolean;
    usePosterAsFallback?: boolean;
    usePlaceholder?: boolean;
    movieImageOptions?: MovieImageOptions;
};

export function getShowingName(
    showing: ShowingDto,
    showingMovie: MovieDto | null,
    locale: string,
    countryCode: ISO_3166_1_LOCALE
): string {
    return (
        getMovieTitle(showingMovie, locale, countryCode) ||
        showing?._embedded?.eventRequest?.name ||
        showing?._embedded?.content?.name ||
        showing?.name
    );
}

export function getShowingDescription(showing: ShowingDto, showingMovie: MovieDto | null, locale: string): string {
    return (
        getMovieOverview(showingMovie, locale) ??
        showing?._embedded?.eventRequest?.description ??
        getLocalizedContentDescription(showing?._embedded?.content, locale)
    );
}

export function getShowingPoster(
    showing: ShowingDto,
    showingMovie: MovieDto | null,
    options?: ShowingImageFilterOptions
): string {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder, movieImageOptions } = options || {};

    const fallbackImage = showing?._embedded?.content?.posterImageUrl ?? showing?.imageUrl;

    return (
        showing?._embedded?.eventRequest?.imageUrl ??
        getMoviePosterImage(showingMovie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: usePlaceholder ?? !fallbackImage,
            imageOptions: movieImageOptions
        }) ??
        fallbackImage
    );
}

export function getShowingPosterBlurDataImage(
    showing?: ShowingDto,
    showingMovie?: MovieDto | null,
    options?: ShowingImageFilterOptions
) {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true } = options || {};

    const fallbackImage = showing?._embedded?.content?.posterImageUrl ?? showing?.imageUrl;

    return (
        showing?._embedded?.eventRequest?.imageUrl ??
        getMoviePosterBlurDataImage(showingMovie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder
        }) ??
        fallbackImage
    );
}

export function getShowingBackdrop(
    showing: ShowingDto,
    showingMovie: MovieDto | null,
    options?: ShowingImageFilterOptions
): string {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder, movieImageOptions } = options || {};

    const fallbackImage =
        showing?._embedded?.content?.backdropImageUrl ??
        showing?.imageUrl ??
        (usePosterAsFallback ? showing?._embedded?.content?.posterImageUrl : undefined);

    return (
        showing?._embedded?.eventRequest?.imageUrl ??
        getMovieBackdropImage(showingMovie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: usePlaceholder ?? !fallbackImage,
            imageOptions: movieImageOptions
        }) ??
        fallbackImage
    );
}

export function getShowingBackdropBlurDataImage(
    showing?: ShowingDto,
    showingMovie?: MovieDto | null,
    options?: ShowingImageFilterOptions
) {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true } = options || {};

    const fallbackImage =
        showing?._embedded?.content?.backdropImageUrl ??
        showing?.imageUrl ??
        (usePosterAsFallback ? showing?._embedded?.content?.posterImageUrl : undefined);

    return (
        showing?._embedded?.eventRequest?.imageUrl ??
        getMovieBackdropBlurDataImage(showingMovie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder
        }) ??
        fallbackImage
    );
}

export function getShowingsProperties(showing: ShowingDto, isMobile?: boolean): string[] {
    const properties: string[] = [];

    if (!showing.isRegularShowtime) {
        properties.push(!isMobile ? 'CINEAMO EVENT' : 'CINEAMO');
    }
    if (showing.isOriginalLanguage) {
        properties.push('OV');
    }
    if (showing.isThreeDimensional) {
        properties.push('3D');
    } else {
        properties.push('2D');
    }

    return properties;
}

export function getShowingPropertiesDimension(showing: ShowingDto | ShowingGroupedByDateTimeDto): string {
    switch (showing.isThreeDimensional) {
        case true:
            return '3D';
        default:
            return '2D';
    }
}

export type ShowingGroupedByDay = {
    date: string;
    showings: ShowingGroupedByDateTimeDto[];
};

export function getShowingsGroupedByDay(groupedShowings: ShowingGroupedByDateTimeDto[]): ShowingGroupedByDay[] {
    const showingsGroupedByDay: Record<string, ShowingGroupedByDateTimeDto[]> = groupedShowings.reduce(
        (result, groupedShowing: ShowingGroupedByDateTimeDto) => {
            const date = formatDate_yyyyMMdd(groupedShowing.startDatetime);
            if (date in result) {
                result[date].push(groupedShowing);
            } else {
                result[date] = [groupedShowing];
            }
            return result;
        },
        {}
    );

    return Object.entries(showingsGroupedByDay).map(([date, showings]) => ({
        date,
        showings
    }));
}

export function getShowingTechnologies(showing: ShowingDto) {
    const technologies = {
        isImax: 'imax',
        isDolbyAtmos: 'dolby-atmos',
        isDolbyVision: 'dolby-vision',
        isDolbyCinema: 'dolby-cinema',
        is4DX: '4-dx',
        isScreenX: 'screen-x',
        isHFR: 'hfr',
        isLive: 'live',
        isDbox: 'd-box'
    };

    return Object.entries(technologies)
        .filter(([key]) => showing[key as keyof ShowingDto])
        .map(([, value]) => value);
}

export function getShowingInfo(options: {
    showing?: ShowingDto;
    content?: CachedContentData;
    countryCode: CountryCode;
    locale: string;
    t: ReturnType<typeof useTranslations>;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    ageRatingText?: string;
}): Array<string> {
    const content = options.content;
    const movieReleaseYear = content.releaseYear;
    const runtimeMinutes = content.runtime;
    const runtimeFormatted = runtimeMinutes ? formatDuration(runtimeMinutes, options.t) : undefined;
    const genres = options.showGenres ? content?.genres : [];

    return [
        movieReleaseYear,
        options.showMovieDuration ? runtimeFormatted : undefined,
        (genres?.slice(0, 3) || []).map((genre) => options.t(`genres.tmdb-genre-${genre.id}`)).join(', '),
        options.ageRatingText
    ].filter((it) => !!it);
}

/**
 * schema.org ScreeningEvent.offers for a showing, or undefined when there is nothing to offer
 * (no ticket URL at all) — callers should omit the `offers` property in that case rather than
 * invent an availability.
 */
export function resolveShowingTicketOffer(
    showing: ShowingDto,
    baseUrl: string
): { url: string; availability: string } | undefined {
    const ticketUrl = getOnlineTicketUrl(showing, Client.CinemaWebsite);
    if (!ticketUrl) {
        return undefined;
    }

    const url = ticketUrl.startsWith('http') ? ticketUrl : `${baseUrl}${ticketUrl}`;
    const availability = isDatetimePast(showing.startDatetime, true)
        ? 'https://schema.org/SoldOut'
        : isShowingSalePending(showing)
          ? 'https://schema.org/PreOrder'
          : 'https://schema.org/InStock';

    return { url, availability };
}
